import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core'

import { BatchPaymentRow, PaymentBatchStateService } from '@modules/private/payment/data/services/view/payment-batch-state.service'
import { APPLICATION_WARNING_LABELS, explainApplicationError } from '@modules/private/application-messages'
import { ApiApplicationService } from '@modules/private/application/data/services/api/api-application.service'

/**
 * Paso 5 · El impacto.
 *
 * Qué le pasa a cada arrendamiento antes de que le pase. Corre el mismo motor que la aplicación, con
 * la transacción abortada, así que lo que se enseña aquí no puede desincronizarse de lo que se
 * escribe después.
 *
 * **En serie y no en paralelo, y esto no es una preferencia.** Una previsualización cuesta ~390 ms
 * contra el clúster de producción (PRD §7.1, medido), y cuarenta a la vez son unas 1,900 consultas
 * simultáneas contra el mismo Mongo que atiende al resto de la plataforma. En serie son ~15 segundos:
 * no es instantáneo, así que el avance es real —«12 de 40»— y se puede cancelar. Un spinner sin
 * número durante quince segundos parece una pantalla colgada.
 */
@Component({
	selector: 'app-step5-impacto',
	templateUrl: './step5-impacto.component.html',
	styleUrls: ['./step5-impacto.component.scss'],
})
export class Step5ImpactoComponent implements OnInit, OnDestroy {
	@Output() onDone = new EventEmitter<void>()
	@Output() onBack = new EventEmitter<void>()
	/** Cerrar el asistente desde la barra de acciones, no solo desde la X de la cabecera. */
	@Output() onExit = new EventEmitter<void>()

	corriendo = false
	/** Cuántos van. Es el numerador de «12 de 40». */
	hechos = 0
	/** Se pidió cancelar: el bucle para al terminar el que tenga en vuelo. */
	private cancelado = false
	cancelando = false

	//* El MISMO diccionario que Exigibilidad. Estaban copiados y ya habían divergido: a la copia de
	//* allá le faltaba `LEASE_WILL_SETTLE` y enseñaba el código en crudo justo al liquidar.
	readonly warningLabels = APPLICATION_WARNING_LABELS

	constructor(
		public readonly state: PaymentBatchStateService,
		private readonly apiApplication: ApiApplicationService
	) {}

	/**
	 * El cálculo arranca solo al entrar. No hay un «Calcular el impacto» que pulsar.
	 *
	 * Era un clic sin alternativa: quien llega aquí viene de decidir los destinos y lo único que
	 * puede querer es ver el impacto. El cálculo **no escribe nada** —corre el motor y aborta la
	 * transacción—, así que no había nada que autorizar; y como va de uno en uno con su avance a la
	 * vista, se puede cancelar en cuanto empieza.
	 *
	 * Al volver desde el paso siguiente no recalcula: `calcular()` salta los pagos que ya traen plan.
	 */
	ngOnInit(): void {
		this.calcular()
	}

	ngOnDestroy(): void {
		//* Salir del paso corta el bucle: si no, seguiría pidiendo previsualizaciones de una pantalla
		//* que ya no existe.
		this.cancelado = true
	}

	/** El operador pidió ver también los que no piden nada. Arranca apagado. */
	verTodos = false

	get objetivo(): BatchPaymentRow[] {
		return this.state.decidedRows
	}

	get total(): number {
		return this.objetivo.length
	}

	get progreso(): number {
		return this.total ? Math.round((this.hechos / this.total) * 100) : 0
	}

	get calculados(): BatchPaymentRow[] {
		return this.objetivo.filter((r) => !!r.preview)
	}

	get conError(): BatchPaymentRow[] {
		return this.objetivo.filter((r) => !!r.previewError)
	}

	/** Lo que se aplicaría en total, sumando el plan de cada pago. */
	get totalPlan(): number {
		return this.calculados.reduce((s, r) => s + this.aplicado(r), 0)
	}

	/** Cuántos arrendamientos quedarían liquidados. Es la advertencia que el owner pidió (D1). */
	get liquidaciones(): BatchPaymentRow[] {
		return this.calculados.filter((r) => this.bloqueantes(r).some((w) => w.code === 'LEASE_WILL_SETTLE'))
	}

	get bloqueantesSinConfirmar(): number {
		return this.calculados.filter((r) => this.state.pendingWarnings(r).length).length
	}

	get puedeSeguir(): boolean {
		return !this.corriendo && this.calculados.length > 0 && this.bloqueantesSinConfirmar === 0
	}

	/** Lo que este pago va a aplicar según su plan. */
	aplicado(row: BatchPaymentRow): number {
		return ((row.preview?.plan ?? []) as any[]).reduce((s, p) => s + Object.values((p?.byConcept ?? {}) as Record<string, number>).reduce((a, v) => a + (Number(v) || 0), 0), 0)
	}

	/**
	 * Los pagos que hay que MIRAR: los que traen algo que confirmar y los que no se pudieron calcular.
	 *
	 * El resto ya está resumido arriba en una cifra. Con cuarenta pagos, listarlos todos obliga a
	 * recorrerlos con la vista para encontrar los dos que piden una decisión — el mismo tedio que este
	 * asistente venía a quitar, un paso más adelante.
	 */
	get destacados(): BatchPaymentRow[] {
		return this.objetivo.filter((r) => !!r.previewError || this.bloqueantes(r).length > 0)
	}

	/** Los que salieron limpios y no piden nada. Se cuentan, y se pueden abrir. */
	get rutinarios(): BatchPaymentRow[] {
		return this.objetivo.filter((r) => !r.previewError && !this.bloqueantes(r).length)
	}

	/**
	 * Qué filas se pintan: solo lo que pide atención, o todo si el operador lo pidió.
	 *
	 * Los rutinarios van SIEMPRE al final, no en el orden del lote. Intercalados, abrirlos metía uno
	 * arriba y otro abajo de los que sí piden algo, y lo que se estaba leyendo cambiaba de sitio.
	 */
	get visibles(): BatchPaymentRow[] {
		return this.verTodos ? [...this.destacados, ...this.rutinarios] : this.destacados
	}

	/** En cuántos arrendamientos distintos cae el dinero: es la frase que resume el lote. */
	get contratosAlcanzados(): number {
		const folios = new Set<string>()
		for (const r of this.calculados) {
			for (const p of (r.preview?.plan ?? []) as any[]) if (p?.leaseId || p?.folio) folios.add(String(p.leaseId ?? p.folio))
		}
		//* Sin `leaseId` en el plan, el mejor dato disponible es a cuántos pagos se les calculó: cada
		//* uno cae al menos en un arrendamiento. Se queda corto antes que inventar de más.
		return folios.size || this.calculados.length
	}

	bloqueantes(row: BatchPaymentRow): any[] {
		return ((row.preview?.warnings ?? []) as any[]).filter((w) => w.blocking)
	}

	/**
	 * El id que une la casilla con su etiqueta. Único por pago y por aviso.
	 *
	 * Hace falta porque esto vive dentro de dos `*ngFor` anidados: un id fijo saldría repetido en cada
	 * fila y todos los `<label for>` apuntarían a la casilla de la primera.
	 */
	idAviso(row: BatchPaymentRow, warning: { code: string }): string {
		return `pbi-warn-${row.paymentId}-${warning.code}`
	}

	etiqueta(code: string): string {
		return this.warningLabels[code] ?? code
	}

	/**
	 * El fallo de un pago, explicado — igual que en Exigibilidad (H-11).
	 *
	 * Aquí solo se enseñaba el mensaje pelado del servidor, así que el MISMO fallo del motor se
	 * explicaba bien en una pantalla y mal en la otra. Y en el asistente importa más: el operador está
	 * mirando cuarenta pagos y necesita saber cuál de los fallos se arregla reintentando.
	 */
	explicado(row: BatchPaymentRow) {
		return explainApplicationError(row.previewError)
	}

	/**
	 * Previsualiza el lote entero, uno detrás de otro.
	 *
	 * Se salta los que ya tienen impacto calculado: volver de una corrección no recalcula cuarenta.
	 */
	async calcular(): Promise<void> {
		if (this.corriendo) return

		this.corriendo = true
		this.cancelado = false
		this.cancelando = false
		this.hechos = 0

		for (const row of this.objetivo) {
			if (this.cancelado) break

			if (row.preview) {
				this.hechos++
				continue
			}

			await this.previsualizar(row)
			this.hechos++
		}

		this.corriendo = false
		this.cancelando = false
	}

	cancelar(): void {
		this.cancelado = true
		this.cancelando = true
	}

	/** Recalcula uno solo. Lo usa quien corrigió una advertencia o quiere reintentar el que falló. */
	recalcular(row: BatchPaymentRow): void {
		this.state.invalidatePreview(row)
		this.previsualizar(row)
	}

	private previsualizar(row: BatchPaymentRow): Promise<void> {
		return new Promise((resolve) => {
			this.apiApplication.createPreview(this.state.buildPayload(row, false) as any, true).subscribe((r) => {
				if (r.error || !r.data) {
					row.preview = null
					row.previewError = r.msg || 'No se pudo calcular el impacto.'
				} else {
					row.preview = r.data
					row.previewError = ''
					row.confirmations = {}
				}
				resolve()
			})
		})
	}
}
